import {
  useEffect, useMemo, useRef, useState,
} from 'react';

import { ActiveFilters, FilterGroup } from './types';

import classes from './chipFilterBar.module.css';

type ChipFilterBarProps = {
  filterGroups: FilterGroup<any>[];
  activeFilters: ActiveFilters;
  handleFilterChange: (groupId: string, filterName: string | null) => void;
};

const SEARCH_THRESHOLD = 6;

/**
 * Chip-style filter bar. Shows a "+ Add filter" button and one chip per active
 * filter. Each group's label is required when using this component.
 */
export const ChipFilterBar = ({
  filterGroups,
  activeFilters,
  handleFilterChange,
}: ChipFilterBarProps) => {
  const activeGroups = filterGroups.filter((g) => activeFilters[g.groupId]);
  const unusedGroups = filterGroups.filter((g) => !activeFilters[g.groupId]);

  return (
    <div className={classes.container}>
      {activeGroups.map((group) => (
        <FilterChip
          key={group.groupId}
          group={group}
          currentValue={activeFilters[group.groupId] as string}
          onChange={(value) => handleFilterChange(group.groupId, value)}
        />
      ))}
      {unusedGroups.length > 0 && (
        <AddFilterButton
          unusedGroups={unusedGroups}
          onSelect={(groupId, value) => handleFilterChange(groupId, value)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// + Add filter button with two-stage popover
// ---------------------------------------------------------------------------

type AddFilterButtonProps = {
  unusedGroups: FilterGroup<any>[];
  onSelect: (groupId: string, value: string) => void;
};

const AddFilterButton = ({ unusedGroups, onSelect }: AddFilterButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [stage, setStage] = useState<{ kind: 'fields' } | { kind: 'values'; groupId: string }>(
    { kind: 'fields' },
  );
  const ref = useRef<HTMLDivElement | null>(null);

  useClosePopover(ref, isOpen, () => {
    setIsOpen(false);
    setStage({ kind: 'fields' });
  });

  const open = () => {
    setStage({ kind: 'fields' });
    setIsOpen(true);
  };

  const selectedGroup = stage.kind === 'values'
    ? unusedGroups.find((g) => g.groupId === stage.groupId)
    : undefined;

  return (
    <div ref={ref} className={classes.addButtonWrapper}>
      <button
        type="button"
        className={classes.addButton}
        onClick={() => (isOpen ? setIsOpen(false) : open())}
      >
        <span className={classes.addIcon}>+</span>
        Add filter
      </button>
      {isOpen && stage.kind === 'fields' && (
        <div className={classes.popover} role="menu">
          <div className={classes.popoverHeader}>Filter by</div>
          <FieldList
            groups={unusedGroups}
            onSelect={(groupId) => setStage({ kind: 'values', groupId })}
          />
        </div>
      )}
      {isOpen && stage.kind === 'values' && selectedGroup && (
        <div className={classes.popover} role="menu">
          <div className={classes.popoverHeader}>
            <button
              type="button"
              className={classes.popoverHeaderBack}
              onClick={() => setStage({ kind: 'fields' })}
            >
              <span className={classes.popoverHeaderChevron}>‹</span>
              Back
            </button>
            <span>·</span>
            <span className={classes.popoverHeaderLabel}>
              {selectedGroup.label ?? selectedGroup.groupId}
            </span>
          </div>
          <ValueList
            group={selectedGroup}
            onPick={(value) => {
              onSelect(selectedGroup.groupId, value);
              setIsOpen(false);
              setStage({ kind: 'fields' });
            }}
          />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Filter chip — shows the active filter, re-opens the value picker on click
// ---------------------------------------------------------------------------

type FilterChipProps = {
  group: FilterGroup<any>;
  currentValue: string;
  onChange: (value: string | null) => void;
};

const FilterChip = ({ group, currentValue, onChange }: FilterChipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useClosePopover(ref, isOpen, () => setIsOpen(false));

  const label = group.label ?? group.groupId;

  return (
    <div ref={ref} className={classes.chipWrapper}>
      <div className={classes.chip}>
        <button
          type="button"
          className={classes.chipBody}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span className={classes.chipLabel}>{label}:</span>
          <span className={classes.chipValue}>{currentValue}</span>
        </button>
        <button
          type="button"
          aria-label={`Remove ${label} filter`}
          className={classes.chipRemove}
          onClick={() => onChange(null)}
        >
          ×
        </button>
      </div>
      {isOpen && (
        <div className={classes.popover} role="menu">
          <div className={classes.popoverHeader}>
            <span className={classes.popoverHeaderLabel}>{label}</span>
          </div>
          <ValueList
            group={group}
            currentValue={currentValue}
            includeClear
            onPick={(value) => {
              onChange(value);
              setIsOpen(false);
            }}
            onClear={() => {
              onChange(null);
              setIsOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Popover inner views
// ---------------------------------------------------------------------------

const FieldList = ({
  groups,
  onSelect,
}: {
  groups: FilterGroup<any>[];
  onSelect: (groupId: string) => void;
}) => (
  <div className={classes.popoverList}>
    {groups.map((group) => (
      <button
        type="button"
        key={group.groupId}
        className={classes.groupRow}
        onClick={() => onSelect(group.groupId)}
      >
        <span className={classes.groupRowLabel}>{group.label ?? group.groupId}</span>
        <span className={classes.groupRowChevron}>›</span>
      </button>
    ))}
  </div>
);

type ValueListProps = {
  group: FilterGroup<any>;
  currentValue?: string;
  includeClear?: boolean;
  onPick: (value: string) => void;
  onClear?: () => void;
};

const ValueList = ({
  group, currentValue, includeClear, onPick, onClear,
}: ValueListProps) => {
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);

  const values = useMemo(
    () => group.filters.map((f) => f.name),
    [group.filters],
  );

  const filtered = useMemo(() => {
    if (!query) return values;
    const q = query.toLowerCase();
    return values.filter((v) => v.toLowerCase().includes(q));
  }, [values, query]);

  const showSearch = values.length > SEARCH_THRESHOLD;

  // Focus the search input on mount so users can immediately type to narrow
  // the list. Done via ref rather than autoFocus (a11y rule).
  useEffect(() => {
    if (showSearch) searchRef.current?.focus();
  }, [showSearch]);

  return (
    <>
      {showSearch && (
        <input
          ref={searchRef}
          type="text"
          className={classes.popoverSearch}
          placeholder="Search..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}
      <div className={classes.popoverList}>
        {includeClear && onClear && (
          <button
            type="button"
            className={`${classes.popoverItem} ${classes.popoverItemClear}`}
            onClick={onClear}
          >
            Clear filter
          </button>
        )}
        {filtered.length === 0 && (
          <div className={classes.popoverEmpty}>No matches</div>
        )}
        {filtered.map((value) => (
          <button
            type="button"
            key={value}
            className={classes.popoverItem}
            onClick={() => onPick(value)}
            disabled={value === currentValue}
          >
            {value}
          </button>
        ))}
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Shared popover close behaviour (click outside + Escape)
// ---------------------------------------------------------------------------

function useClosePopover(
  ref: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  close: () => void,
) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleMouseDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        close();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [ref, isOpen, close]);
}
